import { useState } from 'react';
import { FileText, Search, TrendingUp, Download } from 'lucide-react';
import Header from '../components/layout/Header';
import DomainScanner from '../components/DomainScanner';
import RiskScoreGauge from '../components/RiskScoreGauge';
import BreachTable from '../components/BreachTable';
import { useAuth } from '../contexts/AuthContext';
import { reportApi, ApiError } from '../services/api';
import type { ScanResult } from '../types';

export default function DashboardPage() {
  const { user, isSubscribed } = useAuth();
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);

  const handleScanComplete = (result: ScanResult) => {
    setScanResult(result);
    setReportUrl(null);
  };

  const handleGenerateReport = async () => {
    if (!scanResult) return;

    setGenerating(true);
    try {
      const response = await reportApi.generate(scanResult.scanId);
      if (response.downloadUrl) {
        setReportUrl(response.downloadUrl);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        alert(err.message);
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Header
        title="Dashboard"
        subtitle={`Welcome back${user?.branding?.company_name ? `, ${user.branding.company_name}` : ''}`}
      />

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <FileText className="w-8 h-8 text-teal mb-2 mx-auto" />
            <p className="text-3xl font-tech font-bold">{user?.stats.total_reports || 0}</p>
            <p className="text-sm text-gray-500">Total Reports</p>
          </div>
          <div className="stat-card">
            <Search className="w-8 h-8 text-teal mb-2 mx-auto" />
            <p className="text-3xl font-tech font-bold">{user?.stats.unique_domains || 0}</p>
            <p className="text-sm text-gray-500">Domains Scanned</p>
          </div>
          <div className="stat-card">
            <TrendingUp className="w-8 h-8 text-signal-green mb-2 mx-auto" />
            <p className="text-3xl font-tech font-bold">
              {isSubscribed ? 'Active' : 'Inactive'}
            </p>
            <p className="text-sm text-gray-500">Subscription</p>
          </div>
        </div>

        {/* Quick Scan */}
        <div className="card">
          <h3 className="text-lg font-tech font-semibold mb-4">Quick Domain Scan</h3>
          <DomainScanner onScanComplete={handleScanComplete} />
        </div>

        {/* Scan Results */}
        {scanResult && (
          <div className="card">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-lg font-tech font-semibold">
                  Scan Results: {scanResult.domain}
                </h3>
                <p className="text-sm text-gray-500">
                  {scanResult.cached ? 'Cached result' : 'Fresh scan'} •{' '}
                  {new Date(scanResult.createdAt).toLocaleString()}
                </p>
              </div>

              <div className="flex gap-3">
                {reportUrl ? (
                  <a
                    href={reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary flex items-center gap-2"
                  >
                    <Download size={18} />
                    Download PDF
                  </a>
                ) : (
                  <button
                    onClick={handleGenerateReport}
                    disabled={generating}
                    className="btn-primary flex items-center gap-2"
                  >
                    <FileText size={18} />
                    {generating ? 'Generating...' : 'Generate Report'}
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Risk Score */}
              <div className="flex justify-center">
                <RiskScoreGauge riskScore={scanResult.riskScore} />
              </div>

              {/* Breach Table */}
              <div className="lg:col-span-2">
                <BreachTable breaches={scanResult.breaches} />
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!scanResult && (
          <div className="card text-center py-12">
            <Search size={48} className="mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">
              No scan results yet
            </h3>
            <p className="text-sm text-gray-500">
              Enter a domain above to check for exposed credentials
            </p>
          </div>
        )}
      </div>
    </>
  );
}
